/**
 * Soft loader for `curva-script` Wasm. Missing/unbuilt pkg → `undefined`.
 *
 * Build first: `bun run build:curva-script`
 *
 * In the browser (including Bun HMR), pkgs are loaded from `/wasm/curva-script/`
 * because `import.meta.url` is a `file://` path that browsers refuse to import.
 */

import {
  CURVA_SCRIPT_WASM_PUBLIC_PREFIX,
  shouldLoadWasmPkgOverHttp,
} from "@/wasmPkgPaths";
import type { CurvaDocument } from "@/document/model/document";
import { registerScriptImageFromRgba } from "@/document/assets/scriptImageBridge";
import { ensureScriptFontReady } from "./scriptFonts";
import { measureScriptTextJson } from "./scriptTextMeasure";

export type ScriptLogLevel = "log" | "info" | "error";

export interface ScriptLogEntry {
  level: ScriptLogLevel;
  message: string;
}

export interface ScriptRunResult {
  logs: ScriptLogEntry[];
  error?: string;
  hasUpdate: boolean;
  hasDraw: boolean;
  applied: boolean;
  pagesJson?: string;
  sessionAlive: boolean;
}

export interface ScriptTickResult {
  logs: ScriptLogEntry[];
  error?: string;
  applied: boolean;
  pagesJson?: string;
  exitRequested: boolean;
}

export interface ScriptDocumentContext {
  runId: string;
  frameId: string;
  pages: CurvaDocument["pages"];
  /** Editor selection at script start (node ids). */
  selectedNodeIds?: string[];
  /** Primary selected node id when set. */
  primarySelectedNodeId?: string;
}

export interface CurvaScriptWasm {
  version: string;
  runFrameScript: (source: string, context?: ScriptDocumentContext) => ScriptRunResult;
  startSession: (source: string, context?: ScriptDocumentContext) => ScriptRunResult;
  tickSession: (dtSeconds: number) => ScriptTickResult;
  setInputState: (json: string) => void;
  stopSession: () => void;
  setApplyHandler: (handler: (runId: string, pagesJson: string) => void) => void;
  clearApplyHandler: () => void;
  setImageHandler: (
    handler: (
      runId: string,
      width: number,
      height: number,
      rgba: Uint8Array,
      metaJson: string,
    ) => string,
  ) => void;
  clearImageHandler: () => void;
  setFontHandler?: (handler: (family: string) => boolean) => void;
  clearFontHandler?: () => void;
  setTextMeasureHandler?: (handler: (requestJson: string) => string) => void;
  clearTextMeasureHandler?: () => void;
}

type CurvaScriptPkg = {
  default: (module_or_path?: unknown) => Promise<unknown>;
  curvaScriptVersion: () => string;
  runFrameScript: (source: string, contextJson?: string | null) => string;
  startFrameScriptSession: (source: string, contextJson?: string | null) => string;
  tickFrameScriptSession: (dtSeconds: number) => string;
  setScriptInputState: (json: string) => void;
  stopFrameScriptSession: () => void;
  takeLastScriptTrapMessage?: () => string | undefined | null;
  setScriptApplyHandler: (handler: (runId: string, pagesJson: string) => void) => void;
  clearScriptApplyHandler: () => void;
  setScriptImageHandler: (
    handler: (
      runId: string,
      width: number,
      height: number,
      rgba: Uint8Array,
      metaJson: string,
    ) => string,
  ) => void;
  clearScriptImageHandler: () => void;
  setScriptFontHandler?: (handler: (family: string) => boolean) => void;
  clearScriptFontHandler?: () => void;
  setScriptTextMeasureHandler?: (handler: (requestJson: string) => string) => void;
  clearScriptTextMeasureHandler?: () => void;
};

const FILE_PKG_JS = new URL("./wasm/pkg/curva_script.js", import.meta.url).href;
const FILE_PKG_WASM = new URL("./wasm/pkg/curva_script_bg.wasm", import.meta.url);

function pkgJsHref(): string {
  return shouldLoadWasmPkgOverHttp()
    ? `${CURVA_SCRIPT_WASM_PUBLIC_PREFIX}curva_script.js`
    : FILE_PKG_JS;
}

function pkgWasmUrl(): URL {
  return shouldLoadWasmPkgOverHttp()
    ? new URL(`${CURVA_SCRIPT_WASM_PUBLIC_PREFIX}curva_script_bg.wasm`, globalThis.location?.href ?? "http://localhost/")
    : FILE_PKG_WASM;
}

let loadPromise: Promise<CurvaScriptWasm | undefined> | undefined;

async function importPkg(): Promise<CurvaScriptPkg | undefined> {
  try {
    return (await import(pkgJsHref())) as CurvaScriptPkg;
  } catch {
    return undefined;
  }
}

async function wasmInitInput(): Promise<BufferSource | URL> {
  const wasmUrl = pkgWasmUrl();
  if (typeof Bun !== "undefined" && wasmUrl.protocol === "file:") {
    return await Bun.file(wasmUrl).arrayBuffer();
  }
  return wasmUrl;
}

function contextJson(context?: ScriptDocumentContext): string | undefined {
  return context
    ? JSON.stringify({
      runId: context.runId,
      frameId: context.frameId,
      pages: context.pages,
      selectedNodeIds: context.selectedNodeIds ?? [],
      primarySelectedNodeId: context.primarySelectedNodeId,
    })
    : undefined;
}

function parseLogEntries(raw: unknown): ScriptLogEntry[] {
  if (!Array.isArray(raw)) {
    return [];
  }
  const entries: ScriptLogEntry[] = [];
  for (const entry of raw) {
    if (typeof entry === "string") {
      entries.push({ level: "log", message: entry });
      continue;
    }
    if (!entry || typeof entry !== "object") {
      continue;
    }
    const record = entry as { level?: unknown; message?: unknown };
    if (typeof record.message !== "string") {
      continue;
    }
    const level =
      record.level === "info" || record.level === "error" || record.level === "log"
        ? record.level
        : "log";
    entries.push({ level, message: record.message });
  }
  return entries;
}

function parseRunResult(raw: string): ScriptRunResult {
  const parsed = JSON.parse(raw) as {
    logs?: unknown;
    error?: unknown;
    hasUpdate?: unknown;
    hasDraw?: unknown;
    applied?: unknown;
    pagesJson?: unknown;
    sessionAlive?: unknown;
  };
  return {
    logs: parseLogEntries(parsed.logs),
    error: typeof parsed.error === "string" ? parsed.error : undefined,
    hasUpdate: parsed.hasUpdate === true,
    hasDraw: parsed.hasDraw === true,
    applied: parsed.applied === true,
    pagesJson: typeof parsed.pagesJson === "string" ? parsed.pagesJson : undefined,
    sessionAlive: parsed.sessionAlive === true,
  };
}

function parseTickResult(raw: string): ScriptTickResult {
  const parsed = JSON.parse(raw) as {
    logs?: unknown;
    error?: unknown;
    applied?: unknown;
    pagesJson?: unknown;
    exitRequested?: unknown;
  };
  return {
    logs: parseLogEntries(parsed.logs),
    error: typeof parsed.error === "string" ? parsed.error : undefined,
    applied: parsed.applied === true,
    pagesJson: typeof parsed.pagesJson === "string" ? parsed.pagesJson : undefined,
    exitRequested: parsed.exitRequested === true,
  };
}

function runResultFromTrap(pkg: CurvaScriptPkg, fallback: string): ScriptRunResult {
  const trapped = pkg.takeLastScriptTrapMessage?.() ?? undefined;
  const message = trapped && trapped.length > 0
    ? (trapped.includes("init() error") || trapped.includes("script ") ? trapped : `init() error: ${trapped}`)
    : fallback;
  return {
    logs: [],
    error: message,
    hasUpdate: false,
    hasDraw: false,
    applied: false,
    sessionAlive: false,
  };
}

function tickResultFromTrap(pkg: CurvaScriptPkg, fallback: string): ScriptTickResult {
  const trapped = pkg.takeLastScriptTrapMessage?.() ?? undefined;
  const message = trapped && trapped.length > 0 ? trapped : fallback;
  return {
    logs: [],
    error: message,
    applied: false,
    exitRequested: true,
  };
}

function callRunFrameScript(pkg: CurvaScriptPkg, source: string, context?: ScriptDocumentContext): ScriptRunResult {
  try {
    return parseRunResult(pkg.runFrameScript(source, contextJson(context)));
  } catch (error) {
    // luaur Lua errors panic; wasm32 panic=abort → RuntimeError: unreachable.
    return runResultFromTrap(
      pkg,
      error instanceof Error ? `init() error: ${error.message}` : "init() error: script runtime trap",
    );
  }
}

function callStartSession(pkg: CurvaScriptPkg, source: string, context?: ScriptDocumentContext): ScriptRunResult {
  try {
    return parseRunResult(pkg.startFrameScriptSession(source, contextJson(context)));
  } catch (error) {
    return runResultFromTrap(
      pkg,
      error instanceof Error ? `init() error: ${error.message}` : "init() error: script runtime trap",
    );
  }
}

function callTickSession(pkg: CurvaScriptPkg, dtSeconds: number): ScriptTickResult {
  try {
    return parseTickResult(pkg.tickFrameScriptSession(dtSeconds));
  } catch (error) {
    return tickResultFromTrap(
      pkg,
      error instanceof Error ? error.message : "script runtime trap",
    );
  }
}

export function ensureCurvaScriptWasm(): Promise<CurvaScriptWasm | undefined> {
  if (!loadPromise) {
    loadPromise = (async () => {
      const pkg = await importPkg();
      if (!pkg) {
        return undefined;
      }
      try {
        await pkg.default({ module_or_path: await wasmInitInput() });
        const wasm: CurvaScriptWasm = {
          version: pkg.curvaScriptVersion(),
          runFrameScript: (source, context) => callRunFrameScript(pkg, source, context),
          startSession: (source, context) => callStartSession(pkg, source, context),
          tickSession: dtSeconds => callTickSession(pkg, dtSeconds),
          setInputState: json => {
            pkg.setScriptInputState(json);
          },
          stopSession: () => {
            pkg.stopFrameScriptSession();
          },
          setApplyHandler: handler => {
            pkg.setScriptApplyHandler(handler);
          },
          clearApplyHandler: () => {
            pkg.clearScriptApplyHandler();
          },
          setImageHandler: handler => {
            pkg.setScriptImageHandler(handler);
          },
          clearImageHandler: () => {
            pkg.clearScriptImageHandler();
          },
          setFontHandler: handler => {
            pkg.setScriptFontHandler?.(handler);
          },
          clearFontHandler: () => {
            pkg.clearScriptFontHandler?.();
          },
          setTextMeasureHandler: handler => {
            pkg.setScriptTextMeasureHandler?.(handler);
          },
          clearTextMeasureHandler: () => {
            pkg.clearScriptTextMeasureHandler?.();
          },
        };
        // Always register script image fills with the raster asset map.
        wasm.setImageHandler((_runId, width, height, rgba, metaJson) =>
          registerScriptImageFromRgba({ width, height, rgba, metaJson }),
        );
        // load_font(family): sync readiness check; kicks off load when missing.
        wasm.setFontHandler?.(family => ensureScriptFontReady(family));
        wasm.setTextMeasureHandler?.(requestJson => measureScriptTextJson(requestJson));
        return wasm;
      } catch (error) {
        console.warn("[curva-script] Wasm init failed", error);
        return undefined;
      }
    })();
  }
  return loadPromise;
}

export function resetCurvaScriptWasmForTests(): void {
  loadPromise = undefined;
}
